import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { AppInsightsModule } from 'microsoft-applicationinsights-angular5';
import { EnvironmentNames } from '@src/environments/environments.name';

import { AppBaseModule } from '@src/base-components/app-base.module';
import { OrisRoutingModule } from '@src/clients/oris/oris-routing.module';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';

import { HttpClient } from '@angular/common/http';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';

import { MultiTranslateHttpLoader } from '@src/core/MultiTranslateHttpLoader';
import { AuthConfig } from 'angular-oauth2-oidc';
import { environment } from '@src/environments/environment';

import { PageLayoutComponent } from '@src/base-components/pagelayout/pagelayout.component';
import { OrisComponent } from '@src/clients/oris/oris.component';
import { LoginComponent } from '@src/auth/login.component';
import { PageHeaderComponent } from '@src/shared/page-header/page-header.component';
import { DashboardComponents } from '@src/base-components/dashboard/dashboard.components-container';
import { ActivationComponents } from '@src/base-components/activation/activation.components-container';
import { ActivationComponent } from '@src/clients/oris/activation/activation.component';
import { SaleDetailsCorrectionComponent } from '@src/clients/oris/sales/sale-details/product/sale-details-correction.component';
import { SalesComponents } from '@src/base-components/sales/sales.components-container';
import { RetailersComponents } from '@src/base-components/retailers/retailers.components-container';
import { SidebarOrisComponent } from '@src/clients/oris/sidebar/sidebarOris.component';
import { ProfileComponent } from '@src/base-components/profile/profile.component';
import { ToastCustomOptions } from '@src/clients/oris/extra/toastr.custom-option';
import { ToastrModule } from 'ngx-toastr';
import { PageNotFoundComponent } from '@src/shared/page-notfound.component';
import { FindUserByUsernameComponent } from '@src/base-components/users/find-user/find-user-username.component';
import { UserAccessRoleRules } from '@src/auth/user-access-roles-rules';
import { OrisAccessRules } from '@src/clients/oris/oris-access-rules';
import { SaleDetailsProductOrisComponent } from './sales/sale-details/product/sale-details-product.component';
import { RetailerDetailsInfoOrisComponent } from './retailers/retailer-details/retailer-details-info/retailer-details-info.component';
import { UserDetailsOrisComponent } from './users/user-details/user-details.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { AppSettings } from '@src/core/AppSettings';
import { OrisAppSettings } from '@src/clients/oris/OrisAppSettings';
import { MyPrivacyComponent } from './myprivacy/myprivacy.component';
import { PrivacyComponent } from './privacy/privacy.component';
import { PrivacyModalContentComponent } from '@src/base-components/privacy/privacy-modal-content.component';
import { ManagementComponents } from '@src/base-components/management/management.container';
import { UsersComponents } from '@src/base-components/users/users.container';
import { RepairsComponents } from '@src/base-components/repairs/repairs.container';
import { CustomersOrisComponents } from './customers/customers.components-container';
import { InventoriesComponents } from '@src/base-components/inventories/inventories.components-container';
import { AssistanceComponent } from './assistance/assistance.component';
import { TermsComponent } from './terms/terms.component';
import { FileUploadModule } from 'ng2-file-upload';
import { ExternalSearchOrisComponent } from './external-search/external-search-oris.component';
import { SearchSaleDetailsComponent } from '@src/base-components/external-search/sale-details/sale-details.component';
import { ExternalSearchComponent } from '@src/base-components/external-search/external-search.component';
import { SaleDetailsWarrantiesOrisComponent } from './sales/sale-details/warranties/sale-details-warranties.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ModalReturnSaleOrisComponent } from './sales/sale-details/return-sale/modal-return-sale.component';
import { SaleDetailsOrisComponent } from './sales/sale-details/sale-details.component';
import { SalesOrisComponent } from './sales/sales.component';
import { RecaptchaModule, RECAPTCHA_SETTINGS, RecaptchaSettings, RECAPTCHA_BASE_URL } from 'ng-recaptcha';
import { RecaptchaFormsModule } from 'ng-recaptcha/forms';
import { QRCodePrintingComponents } from '@src/base-components/qr-code-printing/qrcodeprinting.components-container';
import { InventoryItemAddSerialNumberComponent } from '@src/base-components/inventories/inventory-details/items-table/items-add-serial-number/items-add-serial-number';
import { InventoryItemDeleteSerialNumberComponent } from '@src/base-components/inventories/inventory-details/items-table/items-delete-serial-number/items-delete-serial-number';
import { ProductsComponents } from '@src/base-components/products/products.components-container';
import { WizardCreateSerialNumberComponent } from '@src/base-components/wizard/create-searial-number/wizard-create-serial-number.component';
import { SalesFilterOrisComponent } from '@src/clients/oris/sales/filter/sales-filter.component';
import { SalesFilterComponent } from '@src/base-components/sales/filter/sales-filter.component';
import { ShipmentsComponents } from '@src/base-components/shipments/shipments.components-container';
import { ActivationCustomerCreationOrisComponent } from './activation/steps/customer/activation-customer-creation';

export function translateLoader(http: HttpClient) {
    return new MultiTranslateHttpLoader(http, [
        {
            prefix: '/locale/',
            suffix: '.json'
        }, {
            prefix: '/locale/custom/privacy.',
            suffix: '.json'
        }, {
            prefix: '/locale/custom/',
            suffix: '.json'
        }
    ]);
}

let instrumentationKey = '';
if (environment.envName === EnvironmentNames.prod) {
    instrumentationKey = '0b0f9723-36fd-4950-bbbe-e839ded444c9';
} else {
    instrumentationKey = '980f2ac3-0a71-4d60-85a7-cc13237cfa84'; //dev
}

@NgModule({
    imports: [
        AppBaseModule.forRoot(),
        AppInsightsModule.forRoot(instrumentationKey),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (translateLoader),
                deps: [HttpClient]
            }
        }),
        OrisRoutingModule,
        NgxDatatableModule,
        NgxChartsModule,
        ToastrModule.forRoot(ToastCustomOptions),
        FileUploadModule,
        FormsModule,
        ReactiveFormsModule,
        RecaptchaModule,
        RecaptchaFormsModule
    ],
    declarations: [
        PageLayoutComponent,
        PrivacyModalContentComponent,
        PageHeaderComponent,
        OrisComponent,
        PageNotFoundComponent,
        MyPrivacyComponent,
        PrivacyComponent,
        ActivationComponent,
        TermsComponent,
        AssistanceComponent,
        LoginComponent,
        ProfileComponent,
        SearchSaleDetailsComponent,
        FindUserByUsernameComponent,
        InventoryItemAddSerialNumberComponent,
        InventoryItemDeleteSerialNumberComponent,
        SaleDetailsCorrectionComponent,
        SidebarOrisComponent,
        SaleDetailsProductOrisComponent,
        RetailerDetailsInfoOrisComponent,
        ExternalSearchComponent,
        ExternalSearchOrisComponent,
        SaleDetailsWarrantiesOrisComponent,
        ModalReturnSaleOrisComponent,
        SaleDetailsOrisComponent,
        SalesOrisComponent,
        WizardCreateSerialNumberComponent,
        ActivationCustomerCreationOrisComponent,
        UserDetailsOrisComponent,
        ...UsersComponents,
        ...ManagementComponents,
        ...ActivationComponents,
        ...SalesComponents,
        ...RetailersComponents,
        ...DashboardComponents,
        ...RepairsComponents,
        ...QRCodePrintingComponents,
        ...CustomersOrisComponents,
        ...InventoriesComponents,
        ...ProductsComponents,
        ...ShipmentsComponents
    ],
    entryComponents: [PrivacyModalContentComponent, ModalReturnSaleOrisComponent, SalesFilterComponent, SalesFilterOrisComponent ],
    bootstrap: [OrisComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        { provide: AuthConfig, useValue: environment.authConfig },
        { provide: UserAccessRoleRules, useClass: OrisAccessRules },
        { provide: 'AuthService', useClass: environment.authServiceType },
        { provide: AppSettings, useClass: OrisAppSettings },
        {
            provide: RECAPTCHA_SETTINGS,
            useValue: {
                siteKey: environment.siteKey,
            } as RecaptchaSettings,
        },
        {
            provide: RECAPTCHA_BASE_URL,
            useValue: 'https://recaptcha.net/recaptcha/api.js', // use recaptcha.net script source for some of our users
        }
    ]
})
export class OrisModule { }
